import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Grid,
  Box,
  Typography,
  Paper,
  Pagination,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import { EntrySuggestion, MessageGap, VerifiedEntry } from '@/types/api';
import SuggestionCard from './SuggestionCard';
import GapCard from './GapCard';
import InsertDivider from './InsertDivider';
import QuickEntryDialog from './QuickEntryDialog';

interface SuggestionListProps {
  suggestions: EntrySuggestion[];
  gaps?: MessageGap[];
  chatId: number;
  selectedIds: Set<number>;
  onToggleSelect: (messageId: number) => void;
  onReview: (suggestion: EntrySuggestion) => void;
  onViewContext?: (messageId: number) => void;
  onMarkMissing?: (suggestion: EntrySuggestion) => void;
  onCreateMissingFromGap?: (gap: MessageGap, inferredMsNo: string | null, inferredLibrary: number | null) => void;
  libraryId?: number | null;
  onEntryCreated?: (entry: VerifiedEntry) => void;
}

// Union type for merged items
type ListItem =
  | { type: 'suggestion'; data: EntrySuggestion; sortKey: number }
  | { type: 'gap'; data: MessageGap; sortKey: number };

const PAGE_SIZE_OPTIONS = [12, 24, 48, 96];
const DEFAULT_PAGE_SIZE = 24;

const SuggestionList: React.FC<SuggestionListProps> = ({
  suggestions,
  gaps = [],
  chatId,
  selectedIds,
  onToggleSelect,
  onReview,
  onViewContext,
  onMarkMissing,
  onCreateMissingFromGap,
  libraryId,
  onEntryCreated,
}) => {
  const { t } = useTranslation('components');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [insertDialogOpen, setInsertDialogOpen] = useState(false);
  const [insertPosition, setInsertPosition] = useState<{
    beforeId: number;
    afterId: number;
    libraryId: number | null;
  } | null>(null);

  // Merge suggestions and gaps into chronological order
  const mergedItems = useMemo(() => {
    const items: ListItem[] = [];

    // Add suggestions
    suggestions.forEach((suggestion) => {
      items.push({
        type: 'suggestion',
        data: suggestion,
        sortKey: suggestion.title_message_id,
      });
    });

    // Add gaps
    gaps.forEach((gap) => {
      items.push({
        type: 'gap',
        data: gap,
        sortKey: gap.start_message_id,
      });
    });

    // Sort by message ID
    items.sort((a, b) => a.sortKey - b.sortKey);

    return items;
  }, [suggestions, gaps]);

  // Calculate pagination
  const totalPages = Math.ceil(mergedItems.length / pageSize);

  // Get current page items
  const paginatedItems = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return mergedItems.slice(startIndex, startIndex + pageSize);
  }, [mergedItems, page, pageSize]);

  // Reset to page 1 when items change significantly
  React.useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(1);
    }
  }, [mergedItems.length, totalPages, page]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    // Scroll to top of list
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newSize = event.target.value as number;
    setPageSize(newSize);
    setPage(1); // Reset to first page when changing page size
  };

  // Helper to infer library from adjacent cards
  const inferLibraryId = (beforeId: number, afterId: number): number | null => {
    const beforeItem = mergedItems.find((item) => item.sortKey === beforeId);
    const afterItem = mergedItems.find((item) => item.sortKey === afterId);

    if (beforeItem?.type === 'suggestion' && beforeItem.data.library_matches?.[0]?.library_id) {
      return beforeItem.data.library_matches[0].library_id;
    }
    if (afterItem?.type === 'suggestion' && afterItem.data.library_matches?.[0]?.library_id) {
      return afterItem.data.library_matches[0].library_id;
    }
    return libraryId || null;
  };

  const handleOpenInsertDialog = (beforeId: number, afterId: number) => {
    const inferred = inferLibraryId(beforeId, afterId);
    setInsertPosition({ beforeId, afterId, libraryId: inferred });
    setInsertDialogOpen(true);
  };

  const handleCloseInsertDialog = () => {
    setInsertDialogOpen(false);
    setInsertPosition(null);
  };

  const handleEntryCreated = (entry: VerifiedEntry) => {
    setInsertDialogOpen(false);
    setInsertPosition(null);
    onEntryCreated?.(entry);
  };

  if (mergedItems.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          {t('suggestionList.noSuggestions')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('suggestionList.noSuggestionsHint')}
        </Typography>
      </Paper>
    );
  }

  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, mergedItems.length);

  return (
    <Box>
      {/* Header with count and page size selector */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          {t('suggestionList.suggestionsFound', { count: suggestions.length })}
          {gaps.length > 0 && (
            <Typography component="span" variant="body2" color="warning.main" sx={{ ml: 2 }}>
              ({gaps.length} {t('suggestionList.gaps', 'gaps detected')})
            </Typography>
          )}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('suggestionList.showing', { start: startItem, end: endItem, total: mergedItems.length })}
          </Typography>

          <FormControl size="small" sx={{ minWidth: 80 }}>
            <InputLabel id="page-size-label">{t('suggestionList.perPage', 'Per page')}</InputLabel>
            <Select
              labelId="page-size-label"
              value={pageSize}
              label={t('suggestionList.perPage', 'Per page')}
              onChange={handlePageSizeChange as any}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Suggestion cards and gap cards grid with dividers */}
      <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
        {paginatedItems.map((item, index) => (
          <Grid
            item
            xs={12}
            md={6}
            lg={4}
            key={item.type === 'suggestion' ? `suggestion-${item.data.title_message_id}` : `gap-${item.data.start_message_id}-${index}`}
            sx={{ display: 'flex', position: 'relative' }}
          >
            {item.type === 'suggestion' ? (
              <SuggestionCard
                suggestion={item.data}
                chatId={chatId}
                selected={selectedIds.has(item.data.title_message_id)}
                onToggleSelect={onToggleSelect}
                onReview={onReview}
                onViewContext={onViewContext}
                onMarkMissing={onMarkMissing}
              />
            ) : (
              <GapCard
                gap={item.data}
                suggestions={suggestions}
                onCreateMissing={onCreateMissingFromGap || (() => {})}
              />
            )}

            {/* Insert divider overlay on right edge */}
            {index < paginatedItems.length - 1 && (
              <InsertDivider
                key={`divider-${item.sortKey}-${paginatedItems[index + 1].sortKey}`}
                beforeMessageId={item.sortKey}
                afterMessageId={paginatedItems[index + 1].sortKey}
                onInsert={handleOpenInsertDialog}
              />
            )}
          </Grid>
        ))}
      </Grid>

      {/* Quick entry dialog */}
      {insertPosition && (
        <QuickEntryDialog
          open={insertDialogOpen}
          chatId={chatId}
          libraryId={insertPosition.libraryId}
          beforeMessageId={insertPosition.beforeId}
          afterMessageId={insertPosition.afterId}
          onClose={handleCloseInsertDialog}
          onCreated={handleEntryCreated}
        />
      )}

      {/* Pagination controls */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
            size="large"
          />
        </Box>
      )}
    </Box>
  );
};

export default SuggestionList;
